/**
 * @file MyBatches.jsx
 * @description My Classroom page displaying user's enrolled batches or admin assigned batches.
 * @module components/MyBatches
 */
import React, { useState, useEffect } from 'react';
import { apiGet } from '../utils/apiUtils';
import { useAuth } from '../context/AuthContext';
import {
    FiUsers, FiBook, FiArrowLeft, FiLogOut, FiMenu, FiX,
    FiCode, FiLayers, FiActivity, FiBookOpen, FiArrowRight,
    FiPlus, FiTrendingUp, FiCheckCircle, FiClock, FiUser, FiAward
} from 'react-icons/fi';
import UserAvatar from './UserAvatar';
import Navbar from './layout/Navbar';
import Footer from './layout/Footer';
import { motion } from 'framer-motion';
import { GraduationCap, Trophy } from 'lucide-react';

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component MyBatches
 * @description Fetches and renders the list of batches for the authenticated user in a Bento grid.
 *
 * @param {Object} props
 * @param {Function} props.navigate - Router navigation function.
 * @returns {JSX.Element} The rendered My Classroom page.
 */

const MyBatches = ({ navigate }) => {
    // ── Component State ──
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const { isAuthenticated, user, logout } = useAuth();

    useEffect(() => {
        window.scrollTo(0, 0);
        document.title = "SimpleCode | My Classroom";

        const fetchBatches = async () => {
            try {
                const response = await apiGet('/batches');
                if (response.ok) {
                    const data = await response.json();
                    setBatches(data.batches || []);
                }
            } catch (error) {
                console.error("Error fetching batches:", error);
            } finally {
                setLoading(false);
            }
        };

        if (isAuthenticated) fetchBatches();

        return () => {
            document.title = "SimpleCode";
        };
    }, [isAuthenticated]);

    /* ══════════════════════════════════════════════════════════════
     * DATA & CONFIG
     * ══════════════════════════════════════════════════════════════ */

    const navLinks = [
        { id: "home", label: "Home", action: () => navigate("/") },
        { id: "learning", label: "Learning", action: () => navigate("/learning") },
        { id: "dashboard", label: "Dashboard", action: () => navigate("/dashboard") },
        { id: "dsa", label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { id: "system-design", label: "System Design", action: () => navigate("/system-design") }
    ];

    const footerLinks = [
        { label: "Home", action: () => navigate("/") },
        { label: "DSA Problems", action: () => navigate("/dsa-problems") },
        { label: "System Design", action: () => navigate("/system-design") }
    ];

    if (loading && batches.length === 0) return <MyBatchesSkeleton navigate={navigate} navLinks={navLinks} footerLinks={footerLinks} />;

    return (
        <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex flex-col font-sans relative overflow-hidden">
            {/* ── Background Decorations ── */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden z-0 select-none">
                {/* Other Icons */}
                <GraduationCap className="hidden lg:block absolute top-10 -right-5 w-60 h-60 text-emerald-200 -rotate-12 opacity-50" />
                <Trophy className='hidden lg:block absolute bottom-80 -right-5 w-60 h-60 text-emerald-200 rotate-12 opacity-50' />
            </div>

            <Navbar navigate={navigate} navLinks={navLinks} />

            <main className="flex-1 py-16 relative z-10">
                {/* ── Page Header ── */}
                <section className="mt-8">
                    <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-3">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                            <div>
                                <h1 className="text-3xl text-left md:text-4xl font-bold text-foreground underline decoration-dotted decoration-foreground underline-offset-8">
                                    My <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent underline decoration-dotted decoration-emerald-600 ml-1">Classroom</span>
                                </h1>
                                <p className="text-muted-foreground mt-4 max-w-3xl text-left hidden md:block">
                                    {batches.some(b => b.role === 'admin')
                                        ? 'Manage your assigned batches, oversee student evaluations, and track educational milestones.'
                                        : 'Access your specialized batches, standardized assessments, and collaborative learning resources.'}
                                </p>
                            </div>

                            {/* Optional: Add Batch Action (Only for potential admin features) */}
                            {/* <div className="flex-shrink-0">
                                <button className="flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded font-bold shadow-sm hover:bg-emerald-700 transition-all transform hover:-translate-y-0.5">
                                    <FiPlus className="text-lg" />
                                    <span>Register New Batch</span>
                                </button>
                            </div> */}
                        </div>
                    </div>
                </section>

                {/* ── Batches Grid ── */}
                <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-10">
                    {batches.length === 0 ? (
                        <motion.div
                            className="bg-white border-l-4 border-emerald-600 rounded p-12 text-center shadow-sm max-w-3xl mx-auto"
                        >
                            <div className="w-20 h-20 bg-emerald-50 flex items-center justify-center mx-auto mb-6 rounded-full">
                                <FiUsers className="text-4xl text-emerald-600" />
                            </div>
                            <h2 className="text-2xl font-bold text-foreground mb-3">Not Enrolled Yet</h2>
                            <p className="text-muted-foreground mb-8">
                                You are not currently enrolled in any institute or virtual classroom.
                                Enrolment allows you to take premium assessments and track your learning path with instructors.
                            </p>
                            <button
                                onClick={() => navigate('/support')}
                                className="px-8 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded transition-all shadow-md hover:shadow-lg active:scale-95"
                            >
                                Contact Support for Access
                            </button>
                        </motion.div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {batches.map((batch, index) => (
                                <motion.div
                                    key={batch.id}
                                    className="group bg-white border-l-4 border-emerald-600 rounded p-5 transition-all duration-300 flex flex-col h-full"
                                >
                                    {/* Card Header (Dashboard Style) */}
                                    {/* Inner Green Box (Daily Challenge Style) */}
                                    <div className="bg-emerald-600 rounded p-4 mb-4 relative overflow-hidden flex flex-col justify-between transition-colors duration-300 cursor-pointer">
                                        <div className="absolute right-[-10px] top-[-10px] opacity-20 transform -rotate-12 pointer-events-none transition-transform duration-500">
                                            <FiBookOpen className="w-24 h-24 text-emerald-100" />
                                        </div>
                                        
                                        <div className="relative z-10 text-left">
                                            <div className="flex justify-between items-start gap-3 mb-1">
                                                <h3 className="font-bold text-white text-lg line-clamp-1 leading-tight">
                                                    {batch.name}
                                                </h3>
                                            </div>
                                            <div className="flex items-center gap-2 text-emerald-100 text-xs font-medium">
                                                <span>{batch.institution_name || 'SimpleCode'}</span>
                                                {batch.instructor && (
                                                    <>
                                                        <span className="text-emerald-100/80">|</span>
                                                        <span className="flex items-center gap-1.5 truncate">
                                                            {/* <FiUser className="w-3 h-3" /> */}
                                                            {batch.instructor}
                                                        </span>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <button 
                                            onClick={() => navigate(`/batches/${batch.id}`)}
                                            className="mt-4 w-full py-2 rounded bg-white text-emerald-700 text-xs font-bold hover:bg-emerald-50 transition-all shadow-sm flex items-center justify-center gap-2 relative z-10 active:scale-95"
                                        >
                                            {batch.role === 'admin' ? 'Manage Classroom' : 'Open Classroom'}
                                            <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                                        </button>
                                    </div>

                                    {/* Batch Description */}
                                    <div className="flex-1 items-start gap-2.5 mt-auto">
                                        <p className="text-muted-foreground text-xs leading-relaxed line-clamp-2 text-left font-semibold border-l-2 border-emerald-600 pl-2 bg-accent/98 italic">
                                            {batch.description || 'Access specialized curriculum, take scheduled assessments, and synchronize with your mentor.'}
                                        </p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </div>
            </main>

            <Footer navigate={navigate} navLinks={footerLinks} />
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════
 * SKELETON LOADER
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component MyBatchesSkeleton
 * @description Renders a shimmering skeleton placeholder for the My Classroom page.
 *
 * @param {Object} props
 * @param {Function} props.navigate
 * @param {Array} props.navLinks
 * @param {Array} props.footerLinks
 * @returns {JSX.Element}
 */
const MyBatchesSkeleton = ({ navigate, navLinks, footerLinks }) => (
    <div className="min-h-screen bg-gradient-to-r from-emerald-50 via-teal-50 to-emerald-50 text-foreground flex flex-col font-sans relative overflow-hidden">
        {/* ── Background Decorations ── */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden z-0 select-none opacity-50">
            <svg className="absolute -top-12 -left-12 w-96 h-96 text-emerald-600/5 -rotate-12" fill="currentColor" viewBox="0 0 24 24"><path d="M12 3L1 9l11 6 9-4.91V17h2V9L12 3zM3.89 9.57l7.71 4.21 7.71-4.21-7.71-4.21-7.71 4.21zM12 17.5c-2.49 0-4.5-2.01-4.5-4.5H5.5c0 3.59 2.91 6.5 6.5 6.5s6.5-2.91 6.5-6.5h-2c0 2.49-2.01 4.5-4.5 4.5z"/></svg>
            <FiAward className="absolute top-1/4 -right-16 w-80 h-80 text-emerald-600/5 rotate-12" />
            <FiBook className="absolute bottom-1/4 -left-20 w-72 h-72 text-emerald-600/5 -rotate-6" />
        </div>

        <Navbar navigate={navigate} navLinks={navLinks} />
        <main className="flex-1 py-16 relative z-10">
            <section className="mt-8 animate-pulse">
                <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-3">
                    <div className="h-4 w-32 bg-emerald-100 rounded mb-4" />
                    <div className="h-10 w-64 bg-emerald-200 rounded mb-6" />
                    <div className="h-4 w-3/4 bg-emerald-100 rounded" />
                </div>
            </section>
            <div className="max-w-8xl mx-auto px-4 sm:px-12 lg:px-16 py-10">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3, 4, 5, 6].map(n => (
                        <div key={n} className="bg-white border-l-4 border-emerald-100 rounded p-6 shadow-sm h-64 animate-pulse flex flex-col">
                            <div className="flex justify-between mb-8">
                                <div className="space-y-2 flex-1">
                                    <div className="h-3 w-20 bg-emerald-50 rounded" />
                                    <div className="h-6 w-40 bg-emerald-50 rounded" />
                                </div>
                                <div className="h-6 w-16 bg-emerald-50 rounded" />
                            </div>
                            <div className="space-y-2 mb-8">
                                <div className="h-3 w-full bg-emerald-50 rounded" />
                                <div className="h-3 w-4/5 bg-emerald-50 rounded" />
                            </div>
                            <div className="mt-auto pt-5 border-t border-emerald-50 flex justify-between">
                                <div className="h-6 w-24 bg-emerald-50 rounded" />
                                <div className="h-6 w-12 bg-emerald-50 rounded" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </main>
        <Footer navigate={navigate} navLinks={footerLinks} />
    </div>
);

export default MyBatches;

